/* 독립 재계산. 문항이 구하라는 양(target)을 장면의 점 좌표와 곡선 식에서 코드가 직접 다시 계산한다.
 * 호스트가 고른 측정 도구나 호스트가 적은 잰 값과 무관하게, 호스트가 세운 그림이 정답을 실제로 뒷받침하는지 본다 */
import { ComputeEngine } from '@cortex-js/compute-engine';
import { closeEnough } from './measure';
import { rhsOf } from './target';
import type { Target } from './target';
import type { MathcoreApp, ToolResult } from './tools';
import { registryOf } from './tools';

const ce = new ComputeEngine();
type Pt = [number, number];
const fin = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function liveObject(app: MathcoreApp, id: string): any {
  return app.objectManager?.getObjectById?.(id) ?? (app.objectManager?.getAllObjects?.() ?? []).find((o) => o.id === id);
}

/** 이름의 점 좌표. 교점은 유효 후보의 좌표를 쓴다 */
export function pointAt(app: MathcoreApp, name: string): Pt | null {
  for (const id of registryOf(app).get(name)) {
    const o = liveObject(app, id);
    const pos = o?.position;
    if (o && o.isValid !== false && Array.isArray(pos) && fin(pos[0]) && fin(pos[1])) return [pos[0], pos[1]];
  }
  return null;
}

function lineAt(app: MathcoreApp, name: string): [Pt, Pt] | null {
  const o: any = registryOf(app).objectOf(name);
  const c = o?.coordinates;
  if (Array.isArray(c) && c.length >= 2 && Array.isArray(c[0]) && Array.isArray(c[1]) && fin(c[0][0]) && fin(c[0][1]) && fin(c[1][0]) && fin(c[1][1])) return [[c[0][0], c[0][1]], [c[1][0], c[1][1]]];
  return null;
}

const xOfBound = (app: MathcoreApp, v: number | string | null): number | null => {
  if (typeof v === 'number') return v;
  if (!v) return null;
  const n = Number(v);
  if (Number.isFinite(n)) return n;
  return pointAt(app, v)?.[0] ?? null;
};

/** 식(LaTeX)을 x 에 대한 함수로. 못 읽으면 null */
function fnOf(latex: string): ((x: number) => number) | null {
  let expr: ReturnType<typeof ce.parse>;
  try { expr = ce.parse(latex); } catch { return null; }
  return (x: number) => { const v = Number(expr.subs({ x }).N().valueOf()); return Number.isFinite(v) ? v : NaN; };
}

/** 합성 심프슨. 절댓값의 꺾임 때문에 구간을 충분히 잘게 나눈다 */
function integrate(f: (x: number) => number, a: number, b: number): number | null {
  const n = 1000;
  const h = (b - a) / n;
  let sum = f(a) + f(b);
  for (let i = 1; i < n; i += 1) { const y = f(a + i * h); if (!Number.isFinite(y)) return null; sum += (i % 2 ? 4 : 2) * y; }
  const v = sum * h / 3;
  return Number.isFinite(v) ? v : null;
}

export interface Independent { value: number | null; alt?: number; note: string }

export function independentValue(app: MathcoreApp, target: Target, results: ToolResult[]): Independent {
  const none = (note: string): Independent => ({ value: null, note });
  if (target.kind === 'none') return none('구하는 양이 선언되지 않아 독립 재계산을 못 했다');
  if (target.kind === 'length') {
    const [a, b] = target.points.map((n) => pointAt(app, n));
    if (target.points.length !== 2 || !a || !b) return none(`길이의 두 점 ${target.points.join(', ') || '(없음)'} 의 좌표를 읽지 못했다`);
    return { value: Math.hypot(a[0] - b[0], a[1] - b[1]), note: `점 ${target.points.join(', ')} 사이 거리` };
  }
  if (target.kind === 'area_polygon') {
    const pts = target.points.map((n) => pointAt(app, n));
    if (target.points.length < 3 || pts.some((p) => !p)) return none(`다각형 꼭짓점 ${target.points.join(', ') || '(없음)'} 의 좌표를 읽지 못했다`);
    const p = pts as Pt[];
    let twice = 0;
    for (let i = 0; i < p.length; i += 1) { const q = p[(i + 1) % p.length]; twice += p[i][0] * q[1] - q[0] * p[i][1]; }
    return { value: Math.abs(twice) / 2, note: `꼭짓점 ${target.points.join(', ')} 의 신발끈 넓이` };
  }
  if (target.kind === 'angle') {
    const pts = target.points.map((n) => pointAt(app, n));
    if (target.points.length !== 3 || pts.some((p) => !p)) return none(`각의 세 점 ${target.points.join(', ') || '(없음)'} 의 좌표를 읽지 못했다`);
    const [a, v, c] = pts as Pt[];
    const u1: Pt = [a[0] - v[0], a[1] - v[1]]; const u2: Pt = [c[0] - v[0], c[1] - v[1]];
    const rad = Math.acos(Math.max(-1, Math.min(1, (u1[0] * u2[0] + u1[1] * u2[1]) / ((Math.hypot(...u1) * Math.hypot(...u2)) || 1))));
    return { value: rad * 180 / Math.PI, alt: rad, note: `점 ${target.points[1]} 에서 ${target.points[0]}, ${target.points[2]} 가 이루는 각(도, 호도 후보)` };
  }
  if (target.kind === 'gradient') {
    const line = lineAt(app, target.line);
    if (!line) return none(`선 ${target.line || '(없음)'} 의 두 좌표를 읽지 못했다`);
    const dx = line[1][0] - line[0][0];
    if (Math.abs(dx) < 1e-9) return none('수직선이라 기울기가 없다');
    return { value: (line[1][1] - line[0][1]) / dx, note: `선 ${target.line} 의 기울기` };
  }
  // area_under, area_between: 두 곡선(또는 x 축) 사이 넓이를 |f - g| 의 적분으로
  const a = xOfBound(app, target.from); const b = xOfBound(app, target.to);
  if (a === null || b === null) return none(`적분 구간(from ${String(target.from)}, to ${String(target.to)})을 정하지 못했다`);
  const rhsF = rhsOf(results, target.curve);
  if ('problem' in rhsF) return none(rhsF.problem);
  let rhsG = '0';
  if (target.kind === 'area_between') { const g = rhsOf(results, target.other); if ('problem' in g) return none(g.problem); rhsG = g.rhs; }
  const f = fnOf(rhsF.rhs); const g = fnOf(rhsG);
  if (!f || !g) return none('곡선의 식을 읽지 못했다');
  const area = integrate((x) => Math.abs(f(x) - g(x)), Math.min(a, b), Math.max(a, b));
  return area === null ? none('적분 값이 수가 아니다') : { value: area, note: `구간 [${Math.min(a, b).toFixed(3)}, ${Math.max(a, b).toFixed(3)}] 에서 두 곡선 사이 넓이` };
}

export interface IndependentVerdict { verdict: 'ok' | 'mismatch' | 'none'; detail: string }

/** 독립 재계산 값과 맞출 목표(잴 값, 없으면 답)를 비교한다 */
export function judgeIndependent(ind: Independent, goal: number | null): IndependentVerdict {
  if (ind.value === null) return { verdict: 'none', detail: ind.note };
  if (goal === null || !Number.isFinite(goal)) return { verdict: 'none', detail: '맞출 값(잴 값이나 답)이 수가 아니다' };
  if (closeEnough(ind.value, goal) || (ind.alt !== undefined && closeEnough(ind.alt, goal))) return { verdict: 'ok', detail: `독립 재계산 ${ind.value.toFixed(3)} 가 ${goal} 와 같다(${ind.note})` };
  return { verdict: 'mismatch', detail: `독립 재계산 ${ind.value.toFixed(3)} 가 ${goal} 와 다르다(${ind.note})` };
}
